import { useCallback, useEffect, useState } from 'react';
import { useAccount, useSignMessage } from 'wagmi';
import { getPlaysoutAPI } from '../utils/playsoutApi';
import { getApiGameId } from '../data/games';
import { trackLogin } from '../utils/analytics';

/**
 * Playsout API Hook
 * 提供与 Playsout API 交互的便捷方法
 */
export function usePlaysoutAPI() {
  const { address, isConnected } = useAccount();
  const { signMessageAsync } = useSignMessage();
  
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [inviteCode, setInviteCode] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const api = getPlaysoutAPI();

  // 监听钱包地址变化
  useEffect(() => {
    if (address) {
      // 检查钱包是否变更
      const changed = api.checkWalletChange(address);
      if (changed) {
        setIsLoggedIn(false);
        setInviteCode(null);
        console.log('[usePlaysoutAPI] Wallet changed, session cleared');
      } else {
        // 恢复登录状态
        setIsLoggedIn(api.isLoggedIn());
        setInviteCode(api.getInviteCode());
      }
    } else {
      // 钱包断开连接
      setIsLoggedIn(false);
      setInviteCode(null);
    }
  }, [address, api]);

  // 断开连接时清除会话
  useEffect(() => {
    if (!isConnected) {
      api.clearSession();
      setIsLoggedIn(false);
      setInviteCode(null);
    }
  }, [isConnected, api]);

  // 检查是否有有效token并恢复登录状态（不自动触发签名）
  useEffect(() => {
    if (!isConnected || !address) return;
    
    // 检查是否已有有效token
    if (api.isLoggedIn() && !isLoggedIn) {
      setIsLoggedIn(true);
      setInviteCode(api.getInviteCode());
      console.log('[usePlaysoutAPI] Restored login state from token');
    }
  }, [isConnected, address, api, isLoggedIn]);

  /**
   * 登录
   */
  const login = useCallback(async (inputInviteCode?: string): Promise<boolean> => {
    if (!address) {
      setError('Wallet not connected');
      return false;
    }

    // 如果已登录，直接返回成功
    if (api.isLoggedIn()) {
      setIsLoggedIn(true);
      return true;
    }

    setIsLoggingIn(true);
    setError(null);

    try {
      // 如果没有传入邀请码，尝试从URL获取
      let inviteCode = inputInviteCode;
      if (!inviteCode && typeof window !== 'undefined') {
        const urlParams = new URLSearchParams(window.location.search);
        inviteCode = urlParams.get('invite') || undefined;
      }

      await api.login(address, async (message: string) => {
        return await signMessageAsync({ message });
      }, inviteCode);
      
      setIsLoggedIn(true);
      
      // GA4 追踪登录事件
      trackLogin(address, inviteCode);
      
      // 登录成功后获取invite code
      const code = await api.fetchInviteCode();
      if (code) {
        setInviteCode(code);
      }
      
      return true;
    } catch (err: any) {
      const errorMessage = err.message || 'Login failed';
      setError(errorMessage);
      console.error('[usePlaysoutAPI] Login error:', err);
      return false;
    } finally {
      setIsLoggingIn(false);
    }
  }, [address, signMessageAsync, api]);

  /**
   * 发送心跳
   */
  const sendHeartbeat = useCallback(async (gameId: string): Promise<boolean> => {
    if (!isLoggedIn) {
      console.warn('[usePlaysoutAPI] Cannot send heartbeat: not logged in');
      return false;
    }
    return api.sendHeartbeat(gameId);
  }, [isLoggedIn, api]);

  /**
   * 获取invite code
   */
  const fetchInviteCode = useCallback(async (): Promise<string | null> => {
    if (!isLoggedIn) {
      console.warn('[usePlaysoutAPI] Cannot fetch invite code: not logged in');
      return null;
    }
    const code = await api.fetchInviteCode();
    if (code) {
      setInviteCode(code);
    }
    return code;
  }, [isLoggedIn, api]);

  /**
   * 报告交易
   * 注意：不检查 isLoggedIn 状态，避免闭包陷阱导致状态过时
   * API 内部会检查 token，如果未登录会返回 false
   */
  const reportBuy = useCallback(async (gameId: string, txHash: string): Promise<boolean> => {
    return api.reportBuy(gameId, txHash);
  }, [api]);

  /**
   * 登出（清除会话）
   */
  const logout = useCallback(() => {
    api.clearSession();
    setIsLoggedIn(false);
    setInviteCode(null);
    setError(null);
  }, [api]);

  return {
    // 状态
    isLoggedIn,
    isLoggingIn,
    inviteCode,
    error,
    token: api.getToken(),
    
    // 方法
    login,
    logout,
    sendHeartbeat,
    fetchInviteCode,
    reportBuy,
    
    // 工具函数
    getApiGameId,
  };
}
